import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { ClaimAssocText, ClaimFootnote, ClaimFormModel } from "../model/claims-view.model";
import { AppState } from "@shared/redux/store";
import { NgRedux } from "@shared/redux/redux-compatibility.service";
import { Catalog, ReferenceDocument } from "@shared/models/cat.model";
import { ClaimDocMetadataHandler } from "src/app/add-doc-metadata/services/add-doc-metadata-claim.interface";
import { ClaimReferenceDetails } from "../model/claim-reference-details";
import { IntegrationService } from "@shared/services/integration.service";
import { AddDocMetadataComponent } from "src/app/add-doc-metadata/add-doc-metadata.component";
import { SelectChangeEvent } from "primeng/select";
import { ClaimsViewService } from "../service/claims-view.service";
import isEqual from 'fast-deep-equal';
import { BoundingBoxAnchorResult, ClaimReferenceAnchorResult } from '@shared/models/common';
import { CommonService } from "@shared/services/common.service";
import { map  } from "rxjs/operators";
import { of } from "rxjs";


@Component({
    selector: 'claim-form',
    templateUrl: './claim-form.component.html',
    providers: [{
        provide: ClaimDocMetadataHandler, useClass: ClaimDocMetadataHandler
    }],
    styleUrls: ['./claim-form.component.scss'],
    standalone: false
})
export class ClaimFormComponent implements OnInit {
    

    @Input() claim: ClaimFormModel | null = null;
    @Input() catalog!: Catalog;
    @Input() mode: 'create' | 'edit' | 'view' = 'create'

    @Output() onClose = new EventEmitter();
    @Output() refreshView = new EventEmitter();

    @ViewChild('metadataGroup') metadataGroup!: AddDocMetadataComponent; 

    refInitialized: boolean = false;
    filteredRefDocs: ReferenceDocument[] | null = [];
    refDocs: ReferenceDocument[] | null = [];

    associatedText: string = "";
    footnote: string = "";
    statusOptions = [
        { label: 'Draft', value: 'DRAFT' },
        { label: 'Approved', value: 'APPROVED' }
    ];
    contentTypeOptions = [
        { label: 'Claim', value: 'CLAIM'},
        { label: 'Text content', value: 'TEXT_CONTENT'}
    ]
    
    initialClaim: ClaimFormModel | null = null;
    initialMetadata?: { [key: string]: string[] } | null;
    initialized: boolean = false;
    loading: boolean = false;

    constructor(private ngRedux: NgRedux<AppState>, public docMetadataHandler: ClaimDocMetadataHandler, 
        private integrationService: IntegrationService, private claimService: ClaimsViewService, 
        private commonService: CommonService) {}

    
    ngOnInit(): void {

        if (this.claim == null) {
            this.claim = new ClaimFormModel(); 
            this.claim.catVersionId = this.catalog.catVersionId;
        }
        if (this.mode !== 'create') {
            this.docMetadataHandler.claimDocMetadata = this.claim.metadata ?? [];

        }

        // filter already added references
        let refTexts = [];
        if (this.claim.references) {
            
            for (let ref of this.claim.references) {
                if (ref.refPhraseText) {
                    
                    refTexts.push(ref.refPhraseText);
                }
            }
        }
        
        this.integrationService.getAvailableReferenceDocs(this.catVersionId).subscribe((docs: ReferenceDocument[]) => {
            
            // filter refDocs
            this.refInitialized = true;
            this.filteredRefDocs = [];
            if (refTexts.length > 0) {
                this.filteredRefDocs = docs.filter(doc => {
                    const cleanMappedTexts = (doc.mappedReferenceTexts || [])
                        .map(m => m?.trim())
                        .filter(m => !!m);

                    if (cleanMappedTexts.length > 0) {
                        const hasFullMatch = refTexts.some(refText =>
                            cleanMappedTexts.some(m => m === refText)
                        );
                        return !hasFullMatch;
                    } else if (doc.docName) {
                        const cleanDocName = doc.docName.trim();
                        const isDocNameInRefTexts = refTexts.some(refText => refText === cleanDocName);
                        return !isDocNameInRefTexts;
                    }
                    return true;
                });
            } else {
                this.filteredRefDocs = docs;
            }
            this.refDocs = docs;

        })
    }
private isDomNode(value: unknown): value is Node {
    return !!value &&
        typeof value === "object" &&
        "nodeType" in (value as object) &&
        typeof (value as Node).nodeType === "number";
}

// Keeps all normal nested data, but preserves original DOM references
private clonePreservingDomRefs<T>(value: T, seen = new WeakMap<object, any>()): T {
    if (value === null || typeof value !== "object") {
        return value;
    }

    if (this.isDomNode(value)) {
        return value;
    }

    if (seen.has(value as object)) {
        return seen.get(value as object);
    }

    if (Array.isArray(value)) {
        const clone: any[] = [];
        seen.set(value as object, clone);

        for (const item of value as any[]) {
            clone.push(this.clonePreservingDomRefs(item, seen));
        }

        return clone as T;
    }

    const clone: Record<string, any> = {};
    seen.set(value as object, clone);

    for (const key of Object.keys(value as object)) {
        const item = (value as Record<string, any>)[key];
        clone[key] = this.isDomNode(item)
            ? item
            : this.clonePreservingDomRefs(item, seen);
    }

    return clone as T;
}

// Removes DOM properties so we can safely use structuredClone for compare
private sanitizeForComparison<T>(value: T, seen = new WeakMap<object, any>()): T {
    if (value === null || typeof value !== "object") {
        return value;
    }

    if (this.isDomNode(value)) {
        return undefined as unknown as T;
    }

    if (seen.has(value as object)) {
        return seen.get(value as object);
    }

    if (Array.isArray(value)) {
        const clone: any[] = [];
        seen.set(value as object, clone);

        for (const item of value as any[]) {
            const sanitized = this.sanitizeForComparison(item, seen);
            if (!this.isDomNode(sanitized)) {
                clone.push(sanitized);
            }
        }

        return clone as T;
    }

    const clone: Record<string, any> = {};
    seen.set(value as object, clone);

    for (const key of Object.keys(value as object)) {
        const item = (value as Record<string, any>)[key];

        if (this.isDomNode(item)) {
            continue;
        }

        clone[key] = this.sanitizeForComparison(item, seen);
    }

    return clone as T;
}

setInitValues(): void {
    if (this.mode == 'edit') {
        this.initialClaim = this.clonePreservingDomRefs(this.claim);
        this.initialMetadata = structuredClone(this.docMetadataHandler.getDocMetadataToAdd());
    }
}

hasChanges(): boolean {
    const a = structuredClone(this.sanitizeForComparison(this.initialClaim));
    const b = structuredClone(this.sanitizeForComparison(this.claim));

    this.sanitizeForCompare(a);
    this.sanitizeForCompare(b);

    const modelChanged = !isEqual(a, b);

    const currentMetadata = this.docMetadataHandler.getDocMetadataToAdd();
    const metadataChanged = !isEqual(this.initialMetadata, currentMetadata);

    return modelChanged || metadataChanged;
}

discardChanges() {
    if (!this.initialClaim) {
        return;
    }

    this.claim = this.clonePreservingDomRefs(this.initialClaim) as ClaimFormModel;
    this.docMetadataHandler.discardMetadataChanges();
    // restore anchors into class instances...
        if (this.claim?.references && Array.isArray(this.claim.references)) {
            for (const r of this.claim.references) {
                if (Array.isArray(r?.anchors)) {
                    r.anchors = r.anchors.map((a: any) => {
                        const issueId = a?.issueId ?? null;
                        const refDocAnchorId = a?.refDocAnchorId ?? null;
                        const refDocId = a?.refDocId ?? 0;
                        const embedded = (a?.embeddedObjectAnnotation);

                        let inst: any;
                        if (a && (a.phraseIds !== undefined || a.imagesInfos !== undefined)) {
                            inst = new BoundingBoxAnchorResult(issueId, refDocAnchorId, refDocId, embedded);
                        } else {
                            inst = new ClaimReferenceAnchorResult(issueId, refDocAnchorId, refDocId, embedded);
                        }

                        Object.assign(inst, a);
                        return inst;
                    });
                }
            }
        }
}

     
    private sanitizeForCompare(obj: any) {
        if (!obj) {
            return obj;
        }
        // remove metadata (compared separately)
        delete obj.metadata;

        // remove references that have no refDocId
        if (Array.isArray(obj.references)) {
            obj.references = obj.references.filter((r: any) => r && (r.alreadyExists || r.refDocId != null));
        }

        // remove associatedText entries that are empty strings
        if (Array.isArray(obj.associatedText)) {
            obj.associatedText = obj.associatedText.filter((t: any) => {
                const v = (t?.assocPhraseText ?? '').toString().trim();
                return v !== '';
            });
        }

        // remove footnotes entries that are empty strings
        if (Array.isArray(obj.footnotes)) {
            obj.footnotes = obj.footnotes.filter((f: any) => {
                const v = (f?.footnoteText ?? '').toString().trim();
                return v !== '';
            });
        }

        return obj;
    } 


    onSelectPhraseContentType(event : SelectChangeEvent) {

        this.contentTypeOptions = this.contentTypeOptions.filter((option) => option.value == event.value);

        if (this.claim?.contentType == "TEXT_CONTENT") {

            this.claim.associatedText = [];
            this.claim.references = [];
            this.claim.footnotes = [];
        }
    }

    addAssociatedText() {
        if (this.claim && !this.claim.associatedText) {

            this.claim.associatedText = [];
        }
        this.claim?.associatedText.push({
            assocPhraseText: ""
        });
        

    }
    addFootnotes() {
        
        if (this.claim && !this.claim.footnotes) {

            this.claim.footnotes = [];
        }

        this.claim?.footnotes.push({
            footnoteText: ""
        });
        
    }

    addReference() {
        if (!this.claim) {
            return;
        }
        if (this.claim.references == null) {
            this.claim.references = [];
        }

        // empty claim reference
        let claimReference = new ClaimReferenceDetails();
        this.claim.references.push(claimReference);

    }

    removeReference(claimReferenceToRemove: ClaimReferenceDetails) {

        if (this.claim?.references) {

            this.claim.references = this.claim?.references.filter((claimReference) => claimReference != claimReferenceToRemove);

        }
    }

    removeAssociatedText(associatedText: ClaimAssocText) {

        if (this.claim?.associatedText) {
            
            this.claim.associatedText = this.claim?.associatedText.filter((claimAssocText) => claimAssocText != associatedText);
        }
    }
    
    removeFootnotes(footnote: ClaimFootnote) {
        
        if (this.claim?.footnotes) {
            
            this.claim.footnotes = this.claim.footnotes.filter((claimFootnote) => claimFootnote !== footnote);
        }
    }

    isValid() {

        return this.claim && this.claim.claimText && this.claim.claimText !== "" &&
        this.claim.contentType && this.claim.contentType !== "" && this.claim.status && this.claim.status !== "" && this.metadataGroup && this.metadataGroup.canSubmit();
    }
    onSaveClaimChanges() {

        if (!this.claim) {
            return;
        } 

        let metadataToAdd = this.docMetadataHandler.getDocMetadataToAdd();
        //this.claim.metadata = metadataToAdd;
        this.loading = true;
        this.createNotExistingBoundingBoxesAnchors().subscribe(() => {

            this.claimService.saveClaimChanges(this.claim, metadataToAdd).subscribe((response) => {
                // refresh
                this.refreshView.emit();
                this.loading = false;
            },
                (error) => {

                    this.loading = false;
                });
        }, (error) => {

            this.loading = false;
        })
    }
    onEditClaimChanges() {

        if (!this.claim) {
            return;
        }
        let metadataToAdd = this.docMetadataHandler.getDocMetadataToAdd();
        this.loading = true;

        this.createNotExistingBoundingBoxesAnchors().subscribe(() => {


            this.claimService.editClaimChanges(this.claim, metadataToAdd).subscribe((response) => {

                this.refreshView.emit();
                this.loading = false;
            },
                (error) => {
                    this.loading = false;
                })
        }, (error) => {
            this.loading = false;
        })
    }

     createNotExistingBoundingBoxesAnchors() {

        let anchorsToCreate: BoundingBoxAnchorResult[] = [];
        if (this.claim?.references) {
            
            for (const ref of this.claim.references) {

                const boundingBoxAnchors = (ref.anchors ?? []).filter(
                    (anchor): anchor is BoundingBoxAnchorResult =>
                        anchor instanceof BoundingBoxAnchorResult &&
                        anchor.refDocAnchorId == null
                );
                if (boundingBoxAnchors.length > 0) {

                    anchorsToCreate = anchorsToCreate.concat(boundingBoxAnchors);
                }
            }
        }
        if (anchorsToCreate.length == 0) {
            
            return of(null);
        }

         return this.commonService.createBoundingBoxesAnchors(anchorsToCreate, this.catVersionId, null).pipe(map((createdAnchors) => {
             if (Array.isArray(createdAnchors)) {
                 for (let i = 0; i < createdAnchors.length; i++) {
                     
                     anchorsToCreate[i].refDocAnchorId = createdAnchors[i].refDocAnchorId;
                     anchorsToCreate[i].refDocId = createdAnchors[i].refDocId;
                 }
             }
             return null;
         }));
         
        
    }

    close() {
        this.onClose.emit();
    }
    get envId() {
        return this.ngRedux.getState().curEnv?.id;
    }
    get lang() {
        return this.ngRedux.getState().ui_lang
    }
    get catVersionId() {

        return this.catalog?.catVersionId;
    }
    get catSystemVersion() {
        return this.catalog?.systemVersion;
    }

    get title() {
        
        switch(this.mode) {

            case 'create':
                return 'translate.claims-view.createClaim'
            case 'view':
                return 'translate.claims-view.viewClaim'
            default:
                return 'translate.claims-view.editClaim'
        }
    }


}


